import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Form, Formik } from 'formik';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import { UserStatus } from '../../constants/UserStatus';
import FormInputField from '../../common/components/formFields/FormInputField';
import {
  getPaymentMethod,
  getStripeDirectDebitCheckoutSession,
} from '../../mywallet/MyWalletActions';
import { clearStripeValidationMessage } from '../../register/RegisterActions';
import { PaymentMethod } from '../../constants/PaymentMethod';
import plusIcon from '../../assets/img/icons/plus.svg';
import creditCardIcon from '../../assets/img/icons/creditcardicon.svg';
import ChangeCardDetailsModal from './modals/ChangeCardDetailsModal';
import '../../assets/css/componentStyles/PaymentCardPage.css';

const CARD_UPDATE_SUCCESS_TIMEOUT = 3000;
const { CREDIT_CARD, DIRECT_DEBIT, BASED_ON_THE_INVOICE, CREDIT_FACILITY } = PaymentMethod;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function PaymentCardPageInner() {
  const stripe = useStripe();
  const dispatch = useDispatch();

  const last4 = useSelector((state) => state.myWallet.get('last4'));
  const paymentMethod = useSelector((state) => state.myWallet.get('paymentMethod'));
  const userAuthStatus = useSelector((state) => state.auth.get('status'));

  const [isChangeAccepted, setIsChangeAccepted] = useState(false);
  const [cardUpdateSuccess, setCardUpdateSuccess] = useState(false);

  useEffect(() => () => dispatch(clearStripeValidationMessage()), [dispatch]);

  const cardDisplayKey = useMemo(() => `card-${last4}`, [last4]);

  const isBlocked = useMemo(
    () => userAuthStatus === UserStatus.BLOCKED_DUE_TO_PAYMENT,
    [userAuthStatus],
  );

  const isDirectDebitMethod = useMemo(() => paymentMethod === DIRECT_DEBIT, [paymentMethod]);
  const closeModal = useCallback(() => {
    setIsChangeAccepted(false);
  }, []);

  const handleButtonClick = useCallback(() => {
    if (isDirectDebitMethod) {
      dispatch(getStripeDirectDebitCheckoutSession(stripe));
    } else {
      setIsChangeAccepted(true);
    }
  }, [dispatch, stripe, isDirectDebitMethod]);

  const renderBlockedDueToPaymentMessage = useCallback(
    (isDirectDebitMethodPayment = false) => {
      if (isBlocked || last4 === null) {
        return (
          <Alert color="error">
            <p className="m-0">
              We could not charge the payment from your account. Please enter a valid credit
              card number.
            </p>
          </Alert>
        );
      }
      if (last4) {
        return (
          <Alert color="success">
            <p className="m-0">
              {`Thank you for adding the ${
                isDirectDebitMethodPayment ? 'direct debit account' : 'card'
              }. After successful
          verification your account will be activated in a few minutes. If not, please contact
          us.`}
            </p>
          </Alert>
        );
      }
      return null;
    },
    [last4],
  );

  const renderPaymentMethodForm = useCallback(() => {
    switch (paymentMethod) {
      case CREDIT_CARD:
      case CREDIT_FACILITY:
      case BASED_ON_THE_INVOICE:
        return (
          <>
            {isBlocked ? (
              <Row>
                <Col>{renderBlockedDueToPaymentMessage()}</Col>
              </Row>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {last4 === null && !isChangeAccepted && (
                  <FormInputField
                    className="w-100"
                    name="cardNumber"
                    placeholder="Card Number"
                    icon={creditCardIcon}
                    iconClassName="input-right-icon payment-card-icon"
                    disabled={true}
                    label="Credit Card Number"
                    labelVariant="bordered"
                  />
                )}
              </>
            )}
            {last4 && (
              <FormInputField
                key={cardDisplayKey}
                className="w-100"
                name="cardNumber"
                placeholder="Card Number"
                icon={creditCardIcon}
                iconClassName="input-right-icon payment-card-icon"
                disabled={true}
                label="Credit Card Number"
                labelVariant="bordered"
              />
            )}
          </>
        );
      case DIRECT_DEBIT:
        return (
          <div className="d-flex flex-column align-items-center">
            {isBlocked && (
              <Row>
                <Col>{renderBlockedDueToPaymentMessage(true)}</Col>
              </Row>
            )}
            {!last4 && (
              <Alert color="info">
                <p className="text-center mb-5 payment wallet-details__no-card">
                  Your payment method is{' '}
                  <span className="text-uppercase font-weight-bold">Direct Debit</span>. Click{' '}
                  <span className="text-uppercase font-weight-bold">Add</span> to start
                  process.
                </p>
              </Alert>
            )}
            {last4 && (
              <Col md="12" className="d-flex flex-column">
                <FormInputField
                  key={cardDisplayKey}
                  name="cardNumber"
                  placeholder="Card Number"
                  icon={creditCardIcon}
                  iconClassName="input-right-icon payment-card-icon"
                  disabled={!isChangeAccepted}
                />
              </Col>
            )}
          </div>
        );
      default:
        return null;
    }
  }, [
    paymentMethod,
    isBlocked,
    last4,
    isChangeAccepted,
    renderBlockedDueToPaymentMessage,
    cardDisplayKey,
  ]);

  const buttonProps = useMemo(
    () => ({
      className: 'text-sm w-100',
      label: `${last4 ? 'CHANGE CARD DETAILS' : 'ADD CARD'}`,
      variant: last4 ? CommonButtonVariants.OUTLINED_RED : CommonButtonVariants.PRIMARY_RED,
      icon: last4 ? null : plusIcon,
      iconAlt: 'Add New Card',
      iconLeft: !last4,
      handleClick: handleButtonClick,
    }),
    [last4, handleButtonClick],
  );

  const handleCardUpdateSuccess = useCallback(() => {
    setCardUpdateSuccess(true);
    setIsChangeAccepted(false);
    dispatch(getPaymentMethod());
    setTimeout(() => setCardUpdateSuccess(false), CARD_UPDATE_SUCCESS_TIMEOUT);
  }, [dispatch]);

  const initialValues = useMemo(
    () => ({
      cardNumber: last4 ? `**** **** **** ${last4}` : '',
    }),
    [last4],
  );

  return (
    <Formik initialValues={initialValues} enableReinitialize>
      <Form>
        <ChangeCardDetailsModal
          isOpen={isChangeAccepted}
          toggle={closeModal}
          onSuccess={handleCardUpdateSuccess}
        />
        <Row className="align-items-center mt-2">
          {cardUpdateSuccess && (
            <Col md={12} lg={8} className="mt-4">
              <Alert color="success" className="mb-3 payment-card-success-alert">
                Card details updated successfully.
              </Alert>
            </Col>
          )}
        </Row>
        <Row className="align-items-center mt-2">
          <Col md={6} lg={4} className="mt-4">
            {renderPaymentMethodForm()}
          </Col>
          <Col md={6} lg={4} className="d-flex justify-content-end mt-1">
            <CommonButton {...buttonProps} />
          </Col>
        </Row>
      </Form>
    </Formik>
  );
}

function PaymentCardPage() {
  return (
    <Elements stripe={stripePromise}>
      <PaymentCardPageInner />
    </Elements>
  );
}

export default PaymentCardPage;
